/**
 * THE STAGE. One design canvas, one scale factor.
 *
 * 🚨 **THIS IS WHAT MAKES "RESIZES IN PROPORTION" TRUE BY CONSTRUCTION.** Alex, 2026-08-14: *"the
 * game has to resize automatically and in proportion to the window size."*
 *
 * Two earlier attempts did not achieve it and the measurements say why:
 *   · mixing `vh` (scales) with `rem` (does not) - the slot-to-card ratio drifted 0.605 -> 0.470
 *     across three window sizes;
 *   · tying the root font size to the viewport - `min(19vh, 13.5rem)` still switched branches at
 *     different sizes, so the drift remained.
 *
 * **A fixed canvas with one transform is the only construction where the ratios CANNOT drift**, and
 * it is exactly what Unity's CanvasScaler does with a reference resolution. Everything inside is
 * sized in pixels against 1600x900; only `--stage-scale` changes.
 *
 * ⚠️ **`contain` (fit entirely), not `cover`.** A game board must never crop: cropping would hide a
 * slot or a hero at some window shapes, which is a rules problem, not a cosmetic one.
 */

export const STAGE_W = 1600;
export const STAGE_H = 900;

/* ═══════════════════════════════════════════════════════════════════════════════════════════════
 * THE PHONE REFERENCE CANVAS
 *
 * 🚨 **ALEX, 2026-08-18:** *"the revival game needs to be made mobile friendly."*
 *
 * ⛔ **AND IT IS NOT A STYLESHEET PROBLEM, WHICH IS WHY IT NEEDED A CANVAS CHANGE.** Measured on a
 * real iPhone 14 device profile (`tools/verify_mobile.mjs`), landscape, against the 1600x900 canvas:
 *
 *     scale 0.415 on all 7 screens · smallest body text 5px · 15-41 controls under 44px per screen
 *
 * Every size inside the stage is multiplied by that one number, so **no CSS inside the stage can
 * beat the transform wrapping it**: a 14px label is 5.8px on a phone no matter what it is styled to.
 * The reference canvas itself is the only lever.
 *
 * ⭐ **AND THE BINDING CONSTRAINT IS WIDTH, NOT HEIGHT.** min(664/1600, 390/900) = min(0.415, 0.433)
 * - the width loses. That is worth stating because the obvious move, shortening the canvas, buys
 * nothing at all here. Narrowing it is what pays.
 *
 * ⚠️ **A SMALLER CANVAS IS NOT FREE AND THE COST WAS MEASURED BEFORE CHOOSING.** Shrinking the
 * reference improves scale and creates horizontal overflow, because the screens are laid out against
 * the tall canvas: 1600 -> 0 overflowing screens, 1100 -> 4, 900 -> 8, 760 -> 13. So this picks the
 * largest canvas that materially moves the number, and the per-screen layouts follow; it does not
 * pretend one constant finishes the job.
 * ═══════════════════════════════════════════════════════════════════════════════════════════════ */
export const PHONE_STAGE_W = 1100;
export const PHONE_STAGE_H = 620;

/**
 * Is this a phone, as opposed to a small window on a desktop?
 *
 * ⚠️ **`pointer: coarse` AND a small short side - never width alone.** A resized desktop browser at
 * phone dimensions reports `pointer: fine`, and keying on width alone would hand a phone layout to
 * anyone who narrows their window. This is the same test the rotate prompt already uses, and getting
 * it wrong once already produced a confident wrong measurement: a desktop window at 390x844 reported
 * that Revival had no orientation handling at all, when it has a full rotate prompt.
 */
export function isPhone(): boolean {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  const coarse = window.matchMedia("(pointer: coarse)").matches;
  const shortSide = Math.min(window.innerWidth, window.innerHeight);
  return coarse && shortSide <= 500;
}

export function mountStage(host: HTMLElement): HTMLElement {
  // ⛔ **IDEMPOTENT. A SECOND STAGE IS WHY RESIZING "STOPPED WORKING".** Vite's HMR re-runs the
  // entry module, so every hot reload appended ANOTHER `.stage` - the router then drew into the new
  // one while an older, unscaled copy sat underneath it. The board looked like it had stopped
  // responding to the window. Reuse the existing stage rather than creating a rival.
  const existing = host.querySelector<HTMLElement>(":scope > .stage");
  const stage = existing ?? document.createElement("div");
  if (!existing) {
    stage.className = "stage";
    host.append(stage);
  }

  const fit = () => {
    // ⚠️ Measure the HOST, not the window. When this is embedded - a Saloon shell, an iframe - the
    // window is not the space we have, and `innerWidth` would scale the board to somebody else's box.
    const w = host.clientWidth || window.innerWidth;
    const h = host.clientHeight || window.innerHeight;

    // ⭐ The reference canvas is per DEVICE CLASS, not per screen. One canvas for phones and one for
    // everything else keeps the "ratios cannot drift" guarantee intact within each class.
    const phone = isPhone();
    const cw = phone ? PHONE_STAGE_W : STAGE_W;
    const ch = phone ? PHONE_STAGE_H : STAGE_H;

    const s = Math.min(w / cw, h / ch);
    // ⚠️ Guard against 0: a host measured before layout gives scale 0 and an invisible game.
    stage.style.setProperty("--stage-scale", String(s > 0 ? s : 1));
    // The screens size themselves against these, so they must be readable from CSS too.
    stage.style.setProperty("--stage-w", `${cw}px`);
    stage.style.setProperty("--stage-h", `${ch}px`);
    stage.dataset["device"] = phone ? "phone" : "desktop";
  };

  fit();

  // ⚠️ **BOTH SIGNALS, AND ONLY ONE SET OF THEM.** `resize` catches the window; the observer catches
  // a host that changes without it. Previous listeners are dropped first so a hot reload cannot
  // accumulate handlers that fight over the same property.
  cleanup?.();
  const onResize = () => fit();
  window.addEventListener("resize", onResize);
  const ro = new ResizeObserver(fit);
  ro.observe(host);
  // The font can change metrics after load, and an image can change the host's size; re-fit once
  // everything has settled rather than trusting the first frame.
  window.addEventListener("load", onResize, { once: true });

  cleanup = () => {
    window.removeEventListener("resize", onResize);
    ro.disconnect();
  };

  return stage;
}

let cleanup: (() => void) | null = null;
